import axios from 'axios';
import { t } from 'i18next';
import { store } from '../store';
import { getToken } from './';
const API_URL = process.env.REACT_APP_API_URL;

// set headers
const setHeaders = async () => {
  let token = await getToken();
  if (token) {
    axios.defaults.headers.common = { Authorization: `Bearer ${token}` };
  }
};

const surfaceModerationWarning = (data) => {
  if (data?.moderationWarning?.message) {
    store.dispatch({ type: 'HANDLE_RES', payload: { res: data.moderationWarning.message } });
  }
};

/**
 * The HTTP client every screen uses.
 *
 * ## What these methods return, and the one thing that keeps catching people
 *
 * They return the **whole response body**, not a payload. What is inside it depends on which
 * helper the endpoint used to send it, and the platform has two — neither of which is going away:
 *
 * | Backend sends with | Body looks like | Read it as |
 * | --- | --- | --- |
 * | `ApiResponse.success(res, { data })` | `{ data: { ... } }` | `res?.data` |
 * | `httpStatusSender(res, 200, { ... })` | `{ ... }` flat | `res?.field` |
 *
 * Roughly 240 endpoint files use the first and 140 use the second, so neither is the "normal" one
 * and **there is no rule to remember — only the endpoint to check.** Open the handler, look at how
 * it sends, then read the body that way.
 *
 * This is not a hypothetical. The FanCam Challenge landing read a wrapped response as if it were
 * flat, so its caption was always `undefined` and the page rendered "This challenge has ended" for
 * every challenge that has ever existed — including ones a campaign had just sent its whole
 * audience to. The endpoint answered 200 with the caption the entire time. It survived review and a
 * full test suite because the test mocked this client returning an unwrapped object, a shape it
 * cannot produce.
 *
 * So if you are writing a test double for these methods, **mock the body, not the payload.** A mock
 * that skips the envelope will make a broken screen pass.
 *
 * ## Errors
 *
 * A failure does not throw. `handleError` returns a **flattened** object — the response body
 * spread alongside `err` and `status` — so an error detail sits at the top level even when that
 * endpoint's success payload is wrapped. That asymmetry is deliberate and is why error-path reads
 * like `res.resetsAt` are correct beside a success-path read of `res.data.remainingThisHour`.
 *
 * `{ silent: true }` suppresses the global toast so a caller can tell a 404 from a real failure.
 */
const Api = {
  async get(endpoint, button, options) {
    await setHeaders();

    store.dispatch({
      type: 'LOADING_BUTTON',
      payload: button
    });

    try {
      const req = await axios.get(`${API_URL}${endpoint}`);
      if (req.status === 202) {
        store.dispatch({ type: 'HANDLE_RES', payload: { res: req.data } });
        return true;
      }

      store.dispatch({ type: 'LOADING_BUTTON', payload: null });

      return req.data;
    } catch (e) {
      return handleError(e, options);
    }
  },
  async post(endpoint, params, button, options) {
    await setHeaders();

    button &&
      store.dispatch({
        type: 'LOADING_BUTTON',
        payload: button
      });

    try {
      const req = await axios.post(`${API_URL}${endpoint}`, params);
      button && store.dispatch({ type: 'LOADING_BUTTON', payload: null });
      if (req.status === 202) {
        store.dispatch({ type: 'HANDLE_RES', payload: { res: req.data } });
        return true;
      }

      surfaceModerationWarning(req.data);
      return req.data;
    } catch (e) {
      return handleError(e, options);
    }
  },
  async put(endpoint, params, button, options) {
    await setHeaders();

    store.dispatch({
      type: 'LOADING_BUTTON',
      payload: button
    });

    try {
      const req = await axios.put(`${API_URL}${endpoint}`, params);
      store.dispatch({ type: 'LOADING_BUTTON', payload: null });
      if (req.status === 202) {
        store.dispatch({ type: 'HANDLE_RES', payload: { res: req.data } });
        return true;
      }

      store.dispatch({ type: 'LOADING_BUTTON', payload: null });
      surfaceModerationWarning(req.data);
      return req.data;
    } catch (e) {
      return handleError(e, options);
    }
  },
  async patch(endpoint, params, button, options) {
    await setHeaders();

    store.dispatch({
      type: 'LOADING_BUTTON',
      payload: button
    });

    try {
      const req = await axios.patch(`${API_URL}${endpoint}`, params);
      store.dispatch({ type: 'LOADING_BUTTON', payload: null });

      if (req.status === 202) {
        store.dispatch({ type: 'HANDLE_RES', payload: { res: req.data } });
        return true;
      }

      store.dispatch({ type: 'LOADING_BUTTON', payload: null });
      surfaceModerationWarning(req.data);
      return req.data;
    } catch (e) {
      return handleError(e, options);
    }
  },
  async getBlob(endpoint) {
    await setHeaders();
    try {
      const req = await axios.get(`${API_URL}${endpoint}`, { responseType: 'blob' });
      return req.data;
    } catch (e) {
      return handleError(e);
    }
  },
  async delete(endpoint, params, button, options) {
    await setHeaders();

    store.dispatch({
      type: 'LOADING_BUTTON',
      payload: button
    });

    try {
      const req = await axios.delete(`${API_URL}${endpoint}`);
      store.dispatch({ type: 'LOADING_BUTTON', payload: null });

      if (req.status === 202) {
        store.dispatch({ type: 'HANDLE_RES', payload: { res: req.data } });
      }

      store.dispatch({ type: 'LOADING_BUTTON', payload: null });
      // Callers default to a plain success boolean. Pass { raw: true } when the response
      // body matters (e.g. DELETE /admin/fanwave/delete reports what it removed).
      if (options?.raw) return req.data;
      return true;
    } catch (e) {
      return handleError(e, options);
    }
  }
};

// handle api error.
// Pass { silent: true } in options to suppress the global toast and instead return
// { err, status } so the caller can render the error inline (e.g. 409 schedule overlap
// rendered on form fields). 401 always returns response data without toasting.
const handleError = (e, options) => {
  store.dispatch({ type: 'LOADING_BUTTON', payload: null });

  if (e?.response?.status === 401) {
    return e.response.data;
  }

  const status = e?.response?.status;
  const backendErr = e?.response?.data?.err;
  // coded errors (e.g. code: 'LIMIT_EXCEEDED') carry their user-facing text in
  // `message` instead of `err` — toast it as-is
  const code = e?.response?.data?.code;
  const backendMessage = backendErr || (code && e?.response?.data?.message) || null;

  if (options?.silent) {
    if (status >= 500) return { status };

    return { ...(e?.response?.data || {}), err: backendErr, status };
  }

  const errMessage =
    status >= 500 || !backendMessage
      ? t('error.somethingWentWrong') + '\n' + t('error.pleaseTryAgainLater')
      : backendMessage;

  store.dispatch({ type: 'HANDLE_RES', payload: { err: errMessage } });
  return false;
};

export default Api;
